import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/index.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { canonicalRole } from "@/lib/permissions";
import { I18nProvider } from "@/lib/i18n";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Assets from "@/pages/Assets";
import Findings from "@/pages/Findings";
import FindingDetail from "@/pages/FindingDetail";
import ThirdPartyRisk from "@/pages/ThirdPartyRisk";
import ThreatIntel from "@/pages/ThreatIntel";
import MTTRDashboard from "@/pages/MTTRDashboard";
import DnsAnalysis from "@/pages/DnsAnalysis";
import AttackSurface from "@/pages/AttackSurface";
import Reports from "@/pages/Reports";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Compliance from "@/pages/Compliance";
import Settings from "@/pages/Settings";
import AlertRules from "@/pages/AlertRules";
import Scanners from "@/pages/Scanners";
import AssetDiscovery from "@/pages/AssetDiscovery";
import Users from "@/pages/Users";
import Takedowns from "@/pages/Takedowns";
import Layout from "@/components/Layout";
import InactivityWatcher from "@/components/security/InactivityWatcher";

function Protected({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/landing" replace />;
  return <Layout>{children}</Layout>;
}

/** Unauthenticated users see the public landing page; client-scoped users
 *  skip the portfolio list and land on their own client detail; admins /
 *  analysts land on the client portfolio list. */
function ClientAwareRoot() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/landing" replace />;
  if (canonicalRole(user?.role) === "client" && user?.organization_id) {
    return <Navigate to={`/clients/${user.organization_id}`} replace />;
  }
  return <Layout><Clients /></Layout>;
}

function App() {
  return (
    <I18nProvider>
    <AuthProvider>
      <BrowserRouter>
        <Toaster theme="dark" position="top-right" />
        <InactivityWatcher />
        <Routes>
          <Route path="/landing" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<ClientAwareRoot />} />
          <Route path="/clients/:id" element={<Protected><ClientDetail /></Protected>} />
          <Route path="/clients/:id/compliance" element={<Protected><Compliance /></Protected>} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/settings" element={<Protected><Settings /></Protected>} />
          <Route path="/users" element={<Protected><Users /></Protected>} />
          <Route path="/alert-rules" element={<Protected><AlertRules /></Protected>} />
          <Route path="/scanners" element={<Protected><Scanners /></Protected>} />
          <Route path="/attack-surface" element={<Protected><AttackSurface /></Protected>} />
          <Route path="/asset-discovery" element={<Protected><AssetDiscovery /></Protected>} />
          <Route path="/assets" element={<Protected><Assets /></Protected>} />
          <Route path="/findings" element={<Protected><Findings /></Protected>} />
          <Route path="/findings/:id" element={<Protected><FindingDetail /></Protected>} />
          <Route path="/third-party-risk" element={<Protected><ThirdPartyRisk /></Protected>} />
          <Route path="/threat-intel" element={<Protected><ThreatIntel /></Protected>} />
          <Route path="/mttr" element={<Protected><MTTRDashboard /></Protected>} />
          <Route path="/takedowns" element={<Protected><Takedowns /></Protected>} />
          <Route path="/dns-analysis" element={<Protected><DnsAnalysis /></Protected>} />
          <Route path="/reports" element={<Protected><Reports /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </I18nProvider>
  );
}

export default App;
